import React, { useMemo } from "react";

import {
  Container,
  ContainerProps,
} from "@brexhq/next/src/components/layout/Container/Container";
import { BreakPointVals } from "@brexhq/shared/styles/breakpoints";

import { useOptimizedBreakpoint } from "../../hooks/useOptimizedBreakpoint";

interface Props {
  SML: React.ReactNode;
  // MED will fallback to LRG component when not provided
  MED?: React.ReactNode;
  LRG: React.ReactNode;

  additionalContainerProps?: Partial<ContainerProps>;
}

/*
  Note: This component now uses conditional rendering instead of CSS display hiding
  for better performance. Images and other resources are only loaded for the current breakpoint.

  For components that need to be rendered but hidden, use CSS directly:
  const DesktopComponent = styled([...])(({ theme }) => ({
    [theme.breakpoints.SML]: {
      display: "none",
    },
  }));
  const MobileComponent = styled([...])(({ theme }) => ({
    display: "none",
    [theme.breakpoints.SML]: {
      display: "unset",
    },
  }));

  Or use Container display props:
    <Container
      display={{
        SML: "none",
        MED: "none",
        LRG: "unset",
      }}
    >{...}</Container>
*/

/**
 * Optimized container component that conditionally renders content based on breakpoint.
 * Only renders content for the current breakpoint, improving performance by avoiding
 * unnecessary DOM nodes and image loading.
 */
const ResponsiveContainer = React.memo(
  ({
    targetBreakpoint,
    children,
    additionalContainerProps,
  }: {
    targetBreakpoint: BreakPointVals;
    children: React.ReactNode;
    additionalContainerProps?: Partial<ContainerProps>;
  }) => {
    const { shouldRenderOnlyFor } = useOptimizedBreakpoint();

    // Only render if current breakpoint matches target
    if (!shouldRenderOnlyFor(targetBreakpoint)) {
      return null;
    }

    return <Container {...additionalContainerProps}>{children}</Container>;
  },
);

ResponsiveContainer.displayName = "ResponsiveContainer";

/*
  Utility Component for displaying separate components on mobile vs. desktop viewport.
  Now uses conditional rendering for better performance - only renders content for
  the current breakpoint, avoiding unnecessary image loading and DOM nodes.
*/
export const ComponentPerBreakpoint: React.FC<Props> = ({
  SML,
  MED,
  LRG,
  additionalContainerProps,
}) => {
  const { currentBreakpoint } = useOptimizedBreakpoint();

  // Determine which content to render based on current breakpoint
  const content = useMemo(() => {
    switch (currentBreakpoint) {
      case "SML":
        return SML;
      case "MED":
        return MED || LRG; // Fallback to LRG if MED not provided
      case "LRG":
        return LRG;
      default:
        return LRG;
    }
  }, [currentBreakpoint, SML, MED, LRG]);

  return <Container {...additionalContainerProps}>{content}</Container>;
};

// Alternative implementation that maintains separate containers for each breakpoint
// Use this if you need separate containers for each breakpoint with different props
export const ComponentPerBreakpointSeparate: React.FC<Props> = ({
  SML,
  MED,
  LRG,
  additionalContainerProps,
}) => {
  return (
    <>
      <ResponsiveContainer
        targetBreakpoint="LRG"
        additionalContainerProps={additionalContainerProps}
      >
        {LRG}
      </ResponsiveContainer>
      {MED && (
        <ResponsiveContainer
          targetBreakpoint="MED"
          additionalContainerProps={additionalContainerProps}
        >
          {MED}
        </ResponsiveContainer>
      )}
      <ResponsiveContainer
        targetBreakpoint="SML"
        additionalContainerProps={additionalContainerProps}
      >
        {SML}
      </ResponsiveContainer>
    </>
  );
};
